/**
 * AB#22092 — Product development, phase 1: one record per development, the
 * eight-step chain held by the system, every file a version.
 *
 * Five permissions, checked by the backend on every call and here only to
 * show or hide the surfaces:
 *   view-product-development     the menu entry, the list, every record (read)
 *   create-development-request   Products → Submit a request (AB#22169; no longer
 *                                creates a development: that is the PD lead's, from
 *                                an approved request)
 *   scope-development            route, owners, dates; Kick off; any step
 *   work-development-step        a step you own: files, checklist, close, send back
 *   manage-development-settings  Setup → Product development
 *
 * Phase 2b adds comments with mentions, the automatic upload checks (a
 * warning holds the gates until a name acknowledges it), Insights, templates
 * per type code, and "Describe it to Atlas" behind its own permission:
 *   create-development-with-atlas  (backend flag create_development_with_atlas)
 */
import JSZip from 'jszip';
import { ref } from 'vue';

import { can } from '@/composables';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

/** AB#22633 — how the product is produced (Not sure: a request's answer only). */
export type ProductionMethod = 'sublimated' | 'dyed' | 'not_sure';

export const PD_VIEW = 'view-product-development';
export const PD_CREATE = 'create-development-request';
export const PD_SCOPE = 'scope-development';
export const PD_WORK = 'work-development-step';
export const PD_SETTINGS = 'manage-development-settings';
/** Phase 2b — Describe it to Atlas; the backend flag is the gate (permission + flag + key). */
export const PD_ATLAS = 'create-development-with-atlas';

export const developmentAtlasAllowed = (
  features?: { create_development_with_atlas?: boolean } | null
): boolean => features?.create_development_with_atlas === true;

export const canViewProductDevelopment = (): boolean => can(PD_VIEW);
/** AB#22169 — a development is created from an approved request, by the PD lead. */
export const canCreateDevelopment = (): boolean => can(PD_SCOPE);
export const canScopeDevelopment = (): boolean => can(PD_SCOPE);
export const canManageDevelopmentSettings = (): boolean => can(PD_SETTINGS);

export type DevelopmentType = 'new_product' | 'update' | 'config_change';
export type DevelopmentState = 'intake' | 'in_progress' | 'on_hold' | 'live' | 'cancelled';
export type StepState = 'pending' | 'open' | 'waiting' | 'done' | 'skipped';

export interface Person {
  id: number;
  name: string;
}

export interface ChecklistItem {
  label: string;
  ticked_by: number | null;
  ticked_by_name: string | null;
  ticked_at: string | null;
}

export interface DevelopmentStep {
  id: number;
  step: number;
  key: string;
  label: string;
  /** AB#22624 — the branch it runs on (null: before the split or after the rejoin). */
  branch?: 'sample' | '3d' | null;
  /** AB#22871 AC19 — imported as done, the files it asks for that are not there. */
  unbacked?: string[];
  /** AB#22627 — the named contributions it carries, each with its owner. */
  contributions?: Contribution[];
  state: StepState;
  owner: Person | null;
  co_owner: Person | null;
  planned_days: number;
  due_at: string | null;
  /** AB#22373 — set by hand: kept, never recalculated. */
  due_by_hand?: boolean;
  late: boolean;
  opened_at: string | null;
  closed_at: string | null;
  closed_by: number | null;
  checklist: ChecklistItem[];
  checklist_done: number;
  sent_back_from: number | null;
  sent_back_reason: string | null;
  /** AB#22120 — why it was skipped ("Skip physical sample"), when a person skipped it. */
  skipped_reason?: string | null;
  skipped_at?: string | null;
  may_work: boolean;
  /** AB#22205 — what the open step still needs before it can close (empty when nothing). */
  blocked_by?: { key: string; message: string }[];
  /** AB#22218 — the timeline: who moved it on, and what the step means. */
  closed_by_name?: string | null;
  description?: string | null;
  /** AB#22628 AC14 — where the owner came from; the role that does the step (null: anyone in PD). */
  owner_source?: 'rule' | 'default' | 'person' | null;
  role?: string | null;
}

/** AB#22628 AC8 — someone a step may be given, with how many open developments they have on it. */
export interface StepPerson extends Person {
  open: number;
}

/** AB#22628 AC4, AC6 — a rule: on this step, an account, a type or a category names a person. */
export interface AssignmentRule {
  id: number;
  step_key: string;
  match_type: 'company' | 'kind' | 'category';
  match_value: string;
  match_label: string;
  user_id: number;
  user_name: string | null;
  /** The person no longer holds the step's role: not applied. */
  flagged: boolean;
}

export interface Assignment {
  rules: AssignmentRule[];
  offered: Record<string, StepPerson[]>;
  /** AB#22628 AC12 — steps still with someone who left or lost the role. */
  orphans: {
    development_id: number;
    development: string;
    step: number;
    key: string;
    owner: string | null;
  }[];
  step_roles: Record<string, string | null>;
  /** The accounts a rule can name. */
  companies: Person[];
}

/**
 * AB#22225 — one file of the Files section. A file is uploaded or deleted:
 * no versions. `approved` — approved at the pattern approval (step 4); it stays.
 */
/** AB#22368 — a folder people made on a development (none is required). */
export interface DevelopmentFolder {
  id: number;
  parent_id: number | null;
  name: string;
  /** AB#22770 — a standard folder (kept, not renamed), and the step whose people upload into it. */
  standard?: boolean;
  step?: number | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DevelopmentFile {
  id: number;
  kind: string;
  kind_label: string;
  /** AB#22368 — the folder it sits in (null: Home); its type is what counts. */
  folder_id?: number | null;
  /** AB#22122 — the style the file belongs to (null: the development's own). */
  style_id: number | null;
  /** The name of an "other" file's group. */
  label: string | null;
  name: string;
  stored_name: string;
  /** AB#22220 — the size a pattern file is for. */
  size?: string | null;
  /** AB#22226 — the sample round it belongs to. */
  sample_id?: number | null;
  /** AB#22218 — the step it was added at. */
  step?: number | null;
  size_bytes: number | null;
  note: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  approved: boolean;
  approved_at: string | null;
  /** Phase 2b — what the automatic upload checks found, and who looked. */
  checks?: VersionCheck[];
  warnings?: number;
  checks_acknowledged_by?: string | null;
  checks_acknowledged_at?: string | null;
  needs_acknowledgement?: boolean;
  /** AB#22301 — a failed check: the file does not count until it is replaced. */
  failed?: boolean;
  /** AB#22317 — the name does not carry the style code (or carries another), and nobody has confirmed it. */
  flagged?: boolean;
  /** AB#22632 — the package it is in, where inside it (a dragged folder's path), the age group it covers. */
  package_id?: number | null;
  relative_path?: string | null;
  age_group?: string | null;
  /** AB#22637 — its revision level (null: version one, from before), its line, the answer it came with. */
  revision?: string | null;
  lineage_id?: number;
  pattern_instruction?: PatternInstruction | null;
}

/** AB#22637 — Isabelle's words, not ours. */
export type PatternInstruction = 'adjust' | 'no_adjust';
export const PATTERN_INSTRUCTIONS: { value: PatternInstruction; label: string }[] = [
  { value: 'adjust', label: 'Adjust pattern' },
  { value: 'no_adjust', label: 'Do not adjust pattern' },
];

/** AB#22637 — what a new version carries: its level (counted when blank), the answer, what changed. */
export interface RevisionAnswer {
  revision: string;
  instruction: PatternInstruction;
  note: string;
}

/** AB#22637 AC3 — one version of a file, oldest first. */
export interface FileVersionEntry {
  id: number;
  name: string;
  revision: string | null;
  instruction: PatternInstruction | null;
  instruction_label: string | null;
  note: string | null;
  uploaded_by: string | null;
  uploaded_at: string | null;
  current: boolean;
}

/** AB#22632 — several files of one type that are one thing (a TDS, a pattern set). */
export interface DevelopmentPackage {
  id: number;
  kind: string;
  kind_label: string;
  name: string;
  folder_id: number | null;
  file_ids: number[];
  count: number;
  size_bytes: number;
  /** Made from a ZIP: the ZIP as it came, downloadable whole. */
  archive: { name: string; size_bytes: number | null; stored: boolean } | null;
  /** AB#22637 AC5 — a package is versioned as a whole. */
  revision?: string | null;
  version_count?: number;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

/** AB#22632 — a file of a package as it is queued: where it sat, and the age group read from its name. */
export interface PackageEntry {
  file: File;
  path: string | null;
  age: string;
}

/** AB#22225 — a file that left the Files section, for the activity. */
export interface RemovedFile extends DevelopmentFile {
  deleted_reason: 'deleted' | 'replaced' | 'earlier' | 'superseded';
  deleted_by: string | null;
  deleted_at: string | null;
}

/** Phase 2b — one finding of the upload checks. A `warn` holds the gates until acknowledged. */
export interface VersionCheck {
  code: 'kind' | 'size' | 'zip' | 'order' | 'comment' | string;
  /** AB#22301 fail: replaced, never acknowledged away; AB#22317 flag: said, never a block. */
  level: 'fail' | 'warn' | 'info' | 'flag';
  text: string;
  /** AB#22317 — named for another style code: the louder flag. */
  strong?: boolean;
  /** AB#22317 — someone confirmed the flagged file is the right one. */
  confirmed_by_name?: string | null;
  confirmed_at?: string | null;
}

/** Phase 2b — a note on the record, a step or a version. */
export interface DevelopmentComment {
  id: number;
  step: number | null;
  version_id: number | null;
  version_name: string | null;
  by: Person | null;
  body: string;
  mentions: number[];
  at: string | null;
  /** AB#22639 — the thread it answers; changed after the window (and what it said); until when its author may delete it; its files. */
  parent_id?: number | null;
  edited_at?: string | null;
  original_body?: string | null;
  editable_until?: string | null;
  files?: { id: number; name: string; size_bytes: number | null; version_id: number | null }[];
}

/** AB#22639 AC11 — a comment found across developments. */
export interface FoundComment extends DevelopmentComment {
  development: { id: number; name: string } | null;
}

/** Phase 2 — a decision at a gate. */
/** AB#22627 — a named piece of a step's work: its file type, its owner, and whether it is in. */
export interface Contribution {
  key: string;
  label: string;
  kind: string;
  role: string | null;
  owner: Person | null;
  state: 'outstanding' | 'done' | 'not_needed';
  reason: string | null;
}

/** AB#22624 AC6 — a step open now (two while both branches run). */
export interface OpenStep {
  step: number;
  key: string;
  label: string;
  branch: 'sample' | '3d' | null;
  owner: Person | null;
  due_at: string | null;
  late: boolean;
}

export interface Approval {
  id: number;
  gate: 'pattern' | 'go_live';
  /** AB#22627 AC13 — what it was given on. */
  evidence?: 'physical' | 'visual' | 'files' | null;
  evidence_note?: string | null;
  // AB#22222 — the pattern maker may reject the pattern, with a reason (note).
  decision: 'approved' | 'sent_back' | 'rejected';
  version_id: number | null;
  revision_no: number | null;
  by: string | null;
  second_by: string | null;
  pending_second: boolean;
  note: string | null;
  decided_at: string | null;
  at: string | null;
  /** AB#22172 — the role each name signed in, and A3's size confirmation. */
  first_as?: 'pattern_maker' | 'lead_pattern_maker' | 'pd_lead' | null;
  second_as?: 'pattern_maker' | 'lead_pattern_maker' | 'pd_lead' | null;
  sizes_confirmed?: boolean;
}

/** AB#22303 — one entry of a development's Activity, newest first. */
export interface ActivityItem {
  id: string;
  at: string;
  by: string | null;
  kind: string;
  step: number | null;
  step_label: string | null;
  text: string;
  note?: string | null;
  description?: string | null;
  files?: { id: number; name: string; kind_label: string; deleted: boolean }[];
}

/** AB#22172 — the role names of the two-name approvals. */
export const SLOT_LABELS: Record<string, string> = {
  pattern_maker: 'pattern maker',
  lead_pattern_maker: 'lead pattern maker',
  pd_lead: 'PD lead',
};

/** AB#22226 — a prototype in the base size first, the size run once it is approved. */
export type SampleType = 'prototype' | 'size_run';

/** Phase 2 — one sample round of step 3. AB#22226 — rounds count per type. */
export interface SampleRound {
  id: number;
  type: SampleType;
  round: number;
  factory_id: number | null;
  factory_name: string | null;
  ordered_at: string | null;
  received_at: string | null;
  pattern_version_id: number | null;
  /** AB#22387 AC12 — cancelled: called off by the PD lead, with a reason. */
  outcome: 'ordered' | 'in_production' | 'received' | 'approved' | 'rejected' | 'cancelled';
  decision_reason: string | null;
  decided_by: string | null;
  decided_at: string | null;
  fit_notes: string | null;
  stock: Record<string, number> | null;
  /** AB#22375 — the approval given here, the customer's answer, and how it travelled. */
  internal_decision?: 'approved' | 'rejected' | null;
  internal_by?: string | null;
  internal_at?: string | null;
  customer_decision?: 'approved' | 'rejected' | null;
  customer_note?: string | null;
  customer_on?: string | null;
  customer_by?: string | null;
  carrier?: string | null;
  tracking_no?: string | null;
  sent_at?: string | null;
  customer_received_at?: string | null;
  /** AB#22636 — the sizes asked for and how many of each; whether that is the standard sample, and why not. */
  sizes?: SampleSize[] | null;
  sizes_total?: number | null;
  standard?: boolean | null;
  sizes_reason?: string | null;
}

/** AB#22636 — one line of a sample's breakdown. */
export interface SampleSize {
  age_group?: string | null;
  size: string;
  qty: number;
}

/**
 * AB#22375 — where the sample ships (and so who approves it), chosen at
 * kick-off; AB#22387 — or Not needed: no physical sample, no sample step.
 */
export type SampleApprover = 'lead_pattern_maker' | 'customer' | 'both' | 'not_needed';
export const SAMPLE_APPROVERS: { value: SampleApprover; label: string }[] = [
  { value: 'lead_pattern_maker', label: 'Lead pattern maker' },
  { value: 'customer', label: 'Customer' },
  { value: 'both', label: 'Both' },
  { value: 'not_needed', label: 'Not needed' },
];
/** "Shipped to the customer", as the sample step and the summary read it. */
export const approverText = (a: SampleApprover | null | undefined): string =>
  a === 'customer'
    ? 'Shipped to the customer'
    : a === 'both'
      ? 'Shipped to both'
      : a === 'not_needed'
        ? 'No physical sample needed'
        : 'Shipped to the lead pattern maker';

/** AB#22388 — a style code already in the list, as the New development panel offers it. */
export interface ExistingStyleCode {
  id: number;
  code: string;
  state: string;
  product_name: string | null;
  parts: {
    category: string | null;
    year: number | null;
    type: string | null;
    number: number | null;
    age: string | null;
  };
  products: { id: number; name: string; archived: boolean }[];
  /** Retired, or a product of it archived: still allowed, said so. */
  warning: string | null;
}

/** AB#22625 — one style of a product, where it has got to. */
export interface StyleLane {
  id: number;
  style_code: string | null;
  title: string;
  state: DevelopmentState;
  step: string;
  step_key: string | null;
  owner: Person | null;
  due_at: string | null;
  late: boolean;
  done: number;
  of: number;
  through: boolean;
  shares_pattern_of_id: number | null;
  own_production_date: boolean;
  production_date: string | null;
}

/** AB#22625 AC19, AC22, AC24, AC26 — the product's roll-up of its styles. */
export interface StylesRollup {
  release_rule: ReleaseRule;
  styles: StyleLane[];
  by_step: Record<string, number>;
  late: string[];
  slowest: { title: string; step: string; done: number; of: number } | null;
  all_past: number;
  some_past: number;
  all_through_by: string | null;
  waiting_on: string[];
}

/** AB#22625 AC16 — wait for every style, or release each as it is ready. */
export type ReleaseRule = 'all' | 'each';

/** AB#22625 AC28, AC30 — a style's product: its siblings, and the files it holds for every style. */
export interface ProductOfStyle {
  id: number;
  title: string;
  release_rule: ReleaseRule | null;
  state: DevelopmentState;
  siblings: { id: number; title: string; style_code: string | null; state: DevelopmentState }[];
  files: {
    id: number;
    name: string;
    kind: string;
    kind_label: string;
    uploaded_at: string | null;
    uploaded_by: string | null;
  }[];
}

/** AB#22814 — what a merge would do, before anything is written. */
export interface MergePreview {
  title: string;
  from_id: number;
  styles: {
    id: number;
    style_code: string | null;
    title: string;
    proposed: string;
    step: string;
    owner: string | null;
    production_date: string | null;
  }[];
  left_out: { id: number; label: string; why: string }[];
  conflicts: {
    field: 'company' | 'type' | 'production_date';
    takes: { value: string; label: string; from: string[] };
    discards: { value: string; label: string; from: string[] }[];
  }[];
  chosen: { company: string; type: string; production_date: string };
  kept: { id: number; label: string; steps: string[] } | null;
  set_aside: { id: number; label: string; steps: string[] }[];
  changes: string[];
}

/** AB#22814 AC16 — developments that look like one product. */
export interface MergeSuggestion {
  key: string;
  why: string;
  developments: { id: number; label: string }[];
}

export interface MergeBody {
  ids: number[];
  product: { from_id?: number; title?: string };
  choices?: Partial<Record<'company' | 'type' | 'production_date', string>>;
}

export interface DevelopmentRow {
  id: number;
  /** AB#22625 AC25–AC27 — a product reads from its styles; a style names its product. */
  is_product?: boolean;
  style_of?: { id: number; title: string } | null;
  product_styles?: StylesRollup | null;
  /** AB#22624 AC6 — every step open now. */
  open_steps?: OpenStep[];
  pipeline?: 1 | 2;
  style_code: string | null;
  title: string;
  /** AB#22813 — its place in the shared priority order (null: after the ordered ones), and the development owner. */
  queue_position?: number | null;
  owner?: { id: number; name: string } | null;
  /** AB#22530 — the tracker row it was imported from, and whether it still needs checking. */
  external_ref?: string | null;
  needs_review?: boolean;
  type: DevelopmentType;
  type_label: string;
  route: 'full' | 'update';
  state: DevelopmentState;
  priority: 'low' | 'normal' | 'high';
  company: Person | null;
  /** AB#22120 — Generic (for all customers) instead of an account. */
  is_generic?: boolean;
  account_name?: string;
  requested_by: string | null;
  requested_at: string | null;
  target_production_date: string | null;
  current_step: number | null;
  current_step_label: string | null;
  current_owner: Person | null;
  current_due_at: string | null;
  current_late: boolean;
  steps: { step: number; state: StepState; late: boolean }[];
  done_steps: number;
  total_steps: number;
  live_at: string | null;
}

export interface Development extends Omit<DevelopmentRow, 'steps' | 'style_of'> {
  /** AB#22625 — a style's product (with its siblings and files); a style keeping its own production date. */
  style_of?: ProductOfStyle | null;
  own_production_date?: boolean;
  shares_pattern_of?: { id: number; title: string } | null;
  season: string | null;
  kind_code: string | null;
  /** AB#22120 — the parts of Isabelle's style code, and "no physical sample". */
  code_category?: string | null;
  code_year?: number | null;
  code_type?: string | null;
  code_number?: number | null;
  code_age?: string | null;
  skip_sample?: boolean;
  /** AB#22368 — the folders people made. */
  folders?: DevelopmentFolder[];
  /** AB#22373 — the urgency it is planned at, and a date accepted as late on purpose. */
  urgency?: string;
  accepted_late?: boolean;
  request_note: string | null;
  /** AB#22169 — the request it was created from (one development per style). */
  request?: {
    id: number;
    number: string | null;
    product_name: string;
    /** AB#22313 — started without an incoming request, and why. */
    is_internal?: boolean;
    reason?: string | null;
  } | null;
  queue_position: number | null;
  product: Person | null;
  scoped_by: string | null;
  kicked_off_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  steps: DevelopmentStep[];
  files: DevelopmentFile[];
  /** AB#22225 — files that left the Files section: deleted, replaced, or an earlier version. */
  removed_files?: RemovedFile[];
  /** AB#22637 AC8 — the pattern adjustments asked for with a new version, not yet done. */
  pattern_adjustments?: {
    id: number;
    file: string;
    revision: string | null;
    note: string | null;
    requested_by: string | null;
    requested_at: string | null;
  }[];
  /** AB#22632 — packages, and the age groups of the style (a pattern set covers each). */
  packages?: DevelopmentPackage[];
  age_groups?: { value: string; label: string }[];
  /** AB#22634 AC6 — what the factory on it needs from us, present or missing (null: no factory yet). */
  factory_files?: {
    factory: string;
    /** AB#22633 AC9 — the set follows how it is produced. */
    method?: string;
    files: { kind: string; label: string; present: boolean }[];
  } | null;
  /** AB#22770 AC5 — links kept as references; never a file a step requires. */
  links?: { id: number; url: string; label: string | null }[];
  /** AB#22636 — the standard prototype for this style: one size per age group it carries. */
  standard_sample?: SampleSize[];
  /** AB#22633 — how it is produced; null: not answered (it existed before the question). */
  production_method?: ProductionMethod | null;
  production_method_label?: string;
  /** AB#22633 AC5, AC7 — step => the files it expects, for how it is produced. */
  step_files?: Record<string, { kind: string; label: string }[]>;
  can: { scope: boolean; settings: boolean; comment?: boolean };
  // Phase 2 (absent on an older backend)
  released_revision?: number | null;
  approvals?: Approval[];
  samples?: SampleRound[];
  has_approved_sample?: boolean;
  // AB#22226 — where the sample path stands per type (what the step gates read).
  sample_path?: Record<SampleType, SamplePathEntry>;
  // Phase 2b
  comments?: DevelopmentComment[];
  /** AB#22639 AC10 — comments by others this person has not read yet. */
  unread_comments?: number;
  // AB#22121 — base size, the files in use, the product's other developments.
  base_size?: string;
  /** AB#22220 — the size set the pattern files cover. */
  sizes?: string[];
  /** AB#22236 — the style code this development is a variant of. */
  variant_of?: string | null;
  /** AB#22530 — where an imported development came from, as the tracker had it. */
  imported?: {
    external_ref: string;
    import_id: number | null;
    at: string | null;
    tracker: {
      line: number;
      status: string | null;
      requested_by: string | null;
      sport_category: string | null;
      source_blank_number: string | null;
      current_step_state: string | null;
      dates: Record<string, string | null>;
    } | null;
    /** The style code is not in the style code list yet: it can still be set or changed. */
    code_editable?: boolean;
  } | null;
  review_note?: string | null;
  /** AB#22297 — what the variant changes ("Neck", or the words given for other). */
  variant_change?: string | null;
  /** AB#22218 — the order-style header. */
  owner?: { id: number; name: string } | null;
  secondary_owner?: { id: number; name: string } | null;
  csm?: string[];
  customer_reference?: string | null;
  created_by?: string | null;
  created_at?: string | null;
  verification?: Verification;
  product_history?: ProductHistory[];
  // AB#22122 — the development's other styles.
  styles?: DevelopmentStyle[];
  // AB#22172 — the file check before 3D, the approval names this person gives.
  sign?: { pattern: boolean; go_live: boolean; size_run?: boolean; customer_sample?: boolean };
  /** AB#22375 — who approves the sample. */
  sample_approver?: SampleApprover;
  /** AB#22624 — 2: the branched shape; 1: the eight steps it kept. Each branch's days and slack, what holds the rejoin, a pattern changed under 3D. */
  pipeline?: 1 | 2;
  branches?: Record<string, { days: number; slack: number }> | null;
  held_by?: string | null;
  pattern_changed?: {
    at: string;
    round: number | null;
    note: string | null;
    answer: 'redo' | 'keep' | null;
    answer_note: string | null;
    answered_at: string | null;
    /** AB#22766 AC15 — the 3D files that may need redoing. */
    files?: string[];
  } | null;
  /** AB#22766 AC6 — which the master was given as. */
  master_format?: 'SVG' | 'AI' | null;
  /** AB#22627 AC14, AB#22767 AC17 */
  visual_only?: boolean;
  live_without_product?: boolean;
  /** AB#22314 — the pattern is approved: the size run opens. */
  pattern_approved?: boolean;
  /** AB#22317 — files in use whose name does not carry the style code, nobody has confirmed. */
  flagged_files?: number;
  several_styles?: boolean;
}

/** AB#22122 — another style of a development: its own code and TDS. */
export interface DevelopmentStyle {
  id: number;
  title: string;
  style_code: string | null;
  code_category: string | null;
  code_year: number | null;
  code_type: string | null;
  code_number: number | null;
  code_age: string | null;
  tds_file_id: number | null;
  /** AB#22225 — the style's TDS: a file, by name; approved at the pattern approval. */
  tds: { id: number; name: string; stored_name: string; approved: boolean } | null;
}

/** AB#22121 — one line of "files in use". AB#22225 — it names the file, never a version. */
export interface VerificationRow {
  kind: string;
  label: string;
  file: string | null;
  state: 'ok' | 'warn' | 'info';
  text: string;
}
export interface Verification {
  base_size: string;
  rows: VerificationRow[];
}
/** AB#22121 — another development of the same product. */
export interface ProductHistory {
  id: number;
  style_code: string | null;
  title: string;
  type: DevelopmentType;
  state: DevelopmentState;
  live_at: string | null;
  /** AB#22225 — the pattern it approved, by name and date. */
  approved_pattern: string | null;
  pattern_approved_at: string | null;
}
/** AB#22121 — a product found by its SKU code. */
export interface ProductHit {
  id: number;
  name: string;
  company_id: number | null;
  company: string | null;
}

/** AB#22121 — products by SKU code, the account's first (read-only). */
/** AB#22310 — the products a pattern can launch for, each with the style code it runs on today. */
export const searchLaunchProducts = async (
  q: string
): Promise<{ id: number; name: string; brand: string | null; style_code: string | null }[]> => {
  try {
    const { data } = await http.get('product-development/launch-products', { params: { q } });

    return (
      (
        data as {
          result?: {
            items?: { id: number; name: string; brand: string | null; style_code: string | null }[];
          };
        }
      )?.result?.items ?? []
    );
  } catch {
    return [];
  }
};
/** AB#22310 AC6, AB#22311 AC5 — what a move touches. */
export const productsImpact = async (
  ids: number[]
): Promise<{ products: number; accounts: number; open_orders: number } | null> => {
  try {
    const { data } = await http.get('product-development/products-impact', { params: { ids } });

    return (
      (data as { result?: { products: number; accounts: number; open_orders: number } })?.result ??
      null
    );
  } catch {
    return null;
  }
};

export const searchProducts = async (
  q: string,
  companyId?: number | null
): Promise<ProductHit[]> => {
  try {
    const params: Record<string, string | number> = { q };
    if (companyId) params.company_id = companyId;
    const { data } = await http.get('product-development/products', { params });

    return (data as { result?: { items?: ProductHit[] } })?.result?.items ?? [];
  } catch {
    return [];
  }
};

export const SAMPLE_OUTCOMES: { value: SampleRound['outcome']; label: string }[] = [
  { value: 'ordered', label: 'Requested' },
  // AB#22221 — the factory has it in production.
  { value: 'in_production', label: 'In production' },
  { value: 'received', label: 'Received' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

/** AB#22226 — one type's place on the sample path, as the backend reports it. */
export interface SamplePathEntry {
  rounds: number;
  current: { id: number; round: number; outcome: SampleRound['outcome'] } | null;
  approved: boolean;
  next_round: number;
  can_request: boolean;
  why_not: string | null;
}

export const SAMPLE_TYPES: { value: SampleType; label: string }[] = [
  { value: 'prototype', label: 'Prototype' },
  { value: 'size_run', label: 'Size run' },
];

/** The gate steps (phase 2): 4 releases a revision, 7 goes live. */
export const PATTERN_GATE_STEP = 4;
export const GO_LIVE_GATE_STEP = 7;

export interface DevelopmentOptions {
  people: Person[];
  companies: Person[];
  steps: { step: number; key: string; label: string }[];
  routes: { key: string; label: string; skips: number[] }[];
  types: { value: DevelopmentType; label: string }[];
  priorities: { value: string; label: string }[];
  file_kinds: { value: string; label: string }[];
  /** AB#22420 (QA #29) — kind => the file extensions its slot takes (empty: anything). */
  file_extensions?: Record<string, string[]>;
  days: Record<string, Record<string, number>>;
  /** Phase 2b — templates per type code: kind => route => step => days (only what the template sets). */
  days_by_kind?: Record<string, Record<string, Record<string, number>>>;
  /** AB#22633 AC8 — per production method: method => route => step => days (only where it differs). */
  days_by_method?: Record<string, Record<string, Record<string, number>>>;
  kinds?: string[];
  /** AB#22120 — Isabelle's lists, the default owner per step, the sample step, this year. */
  code_lists?: CodeLists;
  default_owners?: Record<string, number>;
  /** AB#22628 AC1, AC8 — step key => who it may be given (holders of its role, and the PD lead). */
  step_people?: Record<string, StepPerson[]>;
  sample_step?: number;
  year?: number;
  me: number;
}

/** AB#22120 — one entry of a code list (Setup also gets whether it is switched on). */
export interface CodeEntry {
  code: string;
  label: string;
  active?: boolean;
}
export interface CodeLists {
  category: CodeEntry[];
  type: CodeEntry[];
  age: CodeEntry[];
}

/** AB#22120 — what GET style-codes/preview answers. */
export interface StyleCodePreview {
  code: string | null;
  number: number | null;
  taken: boolean;
  missing: string[];
}

/** AB#22120 — the code a development will be issued, as the backend composes it (HK6JE64AY). */
export const composeStyleCode = (
  category: string | null | undefined,
  year: number | null | undefined,
  type: string | null | undefined,
  number: number | string | null | undefined,
  age: string | null | undefined
): string | null => {
  if (!category || !type || !age) return null;
  const digit = Math.max(1, Math.min(99, Number(year || new Date().getFullYear()) - 2020));

  return `${category}${digit}${type}${number ? Number(number) : 'NN'}${age}`.toUpperCase();
};

/** "Generic (all customers)", the account's name, or "—". */
export const accountLabel = (d: {
  is_generic?: boolean;
  account_name?: string;
  company: Person | null;
}): string =>
  d.is_generic ? 'Generic (all customers)' : (d.company?.name ?? d.account_name ?? '—');

/** AB#22120 — the file slot a picked file most likely belongs to, from its extension. */
export const guessKind = (name: string, kinds: { value: string }[]): string => {
  const ext = (/\.([a-z0-9]+)$/i.exec(name)?.[1] ?? '').toLowerCase();
  const n = name.toLowerCase();
  const has = (k: string): boolean => kinds.some((x) => x.value === k);
  const pick = (k: string): string => (has(k) ? k : (kinds[0]?.value ?? 'other'));
  // AB#22634 — the factories' types: a blank TDS, Gerber, PLT and print files.
  const tdsName = /\btds\b|tech(nical)?[ _-]?(design|pack)/.test(n);
  if (tdsName && /blank|empty|template/.test(n) && has('tds_blank')) return 'tds_blank';
  if (tdsName) return pick('tds');
  if (['zprj', 'zpac', 'avt', 'glb', 'gltf', 'obj', 'fbx', 'bw', 'usdz'].includes(ext))
    return pick('three_d');
  if ((['mrk', 'plx', 'iso', 'cut'].includes(ext) || /gerber|accumark/.test(n)) && has('gerber'))
    return 'gerber';
  if (['plt', 'hpgl', 'hpg'].includes(ext) && has('plt')) return 'plt';
  if (/\bprint|sublimat/.test(n) && has('print')) return 'print';
  if (['dxf', 'pds', 'plt', 'rul', 'gpz'].includes(ext) || /pattern|patron/.test(n))
    return pick('pattern');
  if (ext === 'cdr' || /corel/.test(n)) return pick('corel');
  if (['jpg', 'jpeg', 'png', 'heic', 'webp'].includes(ext) && /sample|photo|img/.test(n))
    return pick('sample_photo');
  if (['ai', 'eps', 'psd', 'svg'].includes(ext)) return pick('design');
  if (['xlsx', 'xls', 'docx', 'doc'].includes(ext)) return pick('tds');
  if (ext === 'pdf') return pick('tds');

  return pick('other');
};

/** Phase 2b — the days a route plans for a type code: the template over the general setting. */
export const daysForKind = (
  options: Pick<DevelopmentOptions, 'days' | 'days_by_kind' | 'days_by_method'> | null,
  route: string,
  kind: string | null | undefined,
  method?: string | null
): Record<string, number> => {
  const base = { ...(options?.days?.[route] ?? {}) };
  const key = (kind ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const over = key ? (options?.days_by_kind?.[key]?.[route] ?? {}) : {};
  // AB#22633 AC8 — the production method's days last, where it sets them.
  const byMethod = method ? (options?.days_by_method?.[method]?.[route] ?? {}) : {};

  return { ...base, ...over, ...byMethod };
};

export interface Counts {
  in_flight: number;
  intake: number;
  mine: number;
  late: number;
  approval: number;
  new: number;
  updates: number;
  live: number;
  /** AB#22421 (QA #32) */
  cancelled?: number;
  all?: number;
  imported?: number;
}

export const CHIPS: { id: keyof Counts; label: string }[] = [
  { id: 'in_flight', label: 'In flight' },
  { id: 'intake', label: 'Intake' },
  { id: 'mine', label: 'Mine' },
  { id: 'late', label: 'Late' },
  { id: 'approval', label: 'Waiting for approval' },
  { id: 'new', label: 'New products' },
  { id: 'updates', label: 'Updates' },
  { id: 'live', label: 'Live this month' },
  // AB#22421 (QA #32) — cancelled work can be found, and everything at once.
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'all', label: 'All' },
  // AB#22530 AC10 — what came from the tracker.
  { id: 'imported', label: 'Imported' },
];

export const STATE_LABELS: Record<DevelopmentState, string> = {
  intake: 'Intake',
  in_progress: 'In progress',
  on_hold: 'On hold',
  live: 'Live',
  cancelled: 'Cancelled',
};

/** "2026-11-13" → "13 Nov 2026" */
export const dayLabel = (d: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d ?? '');
  if (!m) return d ?? '';
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`;
};

/** Signed working-day distance "due in 2 days" / "3 days late" / "due today". */
export const dueLabel = (due: string | null | undefined, today = new Date()): string => {
  if (!due) return 'no due date';
  const d = new Date(`${due}T00:00:00`);
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (days === 0) return 'due today';
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} late`;

  return `due in ${days} day${days === 1 ? '' : 's'}`;
};

export const sizeLabel = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The due dates the backend will plan at kick-off, for the form's preview:
 * live the working day before production, each earlier step its successor's
 * working days before. Mirrors StepPlanner::dueDates.
 */
export const planDueDates = (
  productionDate: string | null,
  days: Record<string, number>,
  skips: number[]
): Record<number, string | null> => {
  const out: Record<number, string | null> = {};
  for (let n = 1; n <= 8; n++) out[n] = null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(productionDate ?? '');
  if (!m) return out;
  const iso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const isWeekend = (d: Date): boolean => d.getDay() === 0 || d.getDay() === 6;
  const back = (d: Date, n: number): Date => {
    const x = new Date(d);
    while (n > 0) {
      x.setDate(x.getDate() - 1);
      if (!isWeekend(x)) n--;
    }

    return x;
  };
  let due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  due.setDate(due.getDate() - 1);
  while (isWeekend(due)) due.setDate(due.getDate() - 1);
  out[8] = iso(due);
  for (let n = 7; n >= 1; n--) {
    if (skips.includes(n)) continue;
    out[n] = iso(due);
    due = back(due, Number(days[String(n)] ?? 0));
  }

  return out;
};

const result = <T>(data: unknown): T => (data as { result?: T })?.result as T;

export interface WriteResult {
  ok: boolean;
  message: string;
  development?: Development;
  /** AB#22629 — the person cancelled it: nothing was kept, nothing to say as a failure. */
  cancelled?: boolean;
}

/** AB#22629 AC9, AC13 — how far an upload has got, and the way to stop it. */
export interface UploadTracking {
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

/**
 * AB#22629 AC9, AC11 — the bytes PUT to storage with their progress, and
 * cancellable (fetch reports neither).
 */
export const putFile = (
  url: string,
  body: Blob,
  contentType: string,
  t: UploadTracking = {}
): Promise<{ ok: boolean; status: number; aborted: boolean }> =>
  new Promise((resolve) => {
    // Nothing to report and nothing to cancel: a plain PUT does.
    if (!t.onProgress && !t.signal) {
      void fetch(url, { method: 'PUT', body, headers: { 'Content-Type': contentType } })
        .then((r) => resolve({ ok: r.ok, status: r.status, aborted: false }))
        .catch(() => resolve({ ok: false, status: 0, aborted: false }));

      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) =>
      t.onProgress?.(e.loaded, e.lengthComputable ? e.total : body.size);
    xhr.onload = () => {
      t.onProgress?.(body.size, body.size);
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, aborted: false });
    };
    xhr.onerror = () => resolve({ ok: false, status: 0, aborted: false });
    xhr.onabort = () => resolve({ ok: false, status: 0, aborted: true });
    if (t.signal?.aborted) {
      resolve({ ok: false, status: 0, aborted: true });

      return;
    }
    t.signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(body);
  });

/** AB#22629 AC11 — why an upload failed, in words rather than a code. */
export const uploadFailure = (status: number): string =>
  status === 0
    ? 'The connection dropped before the file was stored.'
    : status === 403
      ? 'Storage refused it: the upload link expired.'
      : status === 413
        ? 'Storage refused it: the file is too large.'
        : status >= 500
          ? 'Storage could not take it just now.'
          : `Storage refused it (${status}).`;

export function useProductDevelopment() {
  const items = ref<DevelopmentRow[]>([]);
  const total = ref(0);
  const page = ref(1);
  const perPage = ref(25);
  const lastPage = ref(1);
  const counts = ref<Counts | null>(null);
  const chip = ref<string>('in_flight');
  const q = ref('');
  const ownerId = ref('');
  const companyId = ref('');
  const loading = ref(false);
  const fetching = ref(false);
  const options = ref<DevelopmentOptions | null>(null);
  const current = ref<Development | null>(null);
  const currentLoading = ref(false);
  const busy = ref(false);

  /** AB#22813 AC21 — a column sort, or the shared priority order (the default). */
  const sort = ref<{ key: 'priority' | 'production' | 'code' | 'name'; dir: 'asc' | 'desc' }>({
    key: 'priority',
    dir: 'asc',
  });
  /** AB#22813 AC10 — every development the current filters find (not only the page shown). */
  const filteredIds = async (): Promise<number[]> => {
    const params: Record<string, string | number> = { chip: chip.value, ids_only: 1 };
    if (q.value.trim()) params.q = q.value.trim();
    if (ownerId.value) params.owner_id = ownerId.value;
    if (companyId.value) params.company_id = companyId.value;
    try {
      const { data } = await http.get('product-development', { params });

      return result<{ ids: number[] }>(data)?.ids ?? [];
    } catch {
      return [];
    }
  };
  /** AB#22813 AC1–AC8 — the name, the account or the type, edited in place. */
  const editField = async (id: number, body: Record<string, unknown>): Promise<WriteResult> => {
    try {
      const { data } = await http.patch(`product-development/${id}/field`, body);
      const row = result<{ development: DevelopmentRow }>(data)?.development;
      if (row) items.value = items.value.map((r) => (r.id === id ? { ...r, ...row } : r));
      if (current.value?.id === id) await open(id);

      return { ok: true, message: String((data as { message?: string })?.message ?? '') };
    } catch (e: unknown) {
      return failure(e);
    }
  };
  /** AB#22813 AC13–AC18 — one change on many; the reply says how many, and names what could not take it. */
  const bulkAction = async (body: Record<string, unknown>): Promise<WriteResult> => {
    try {
      const { data } = await http.post('product-development/bulk', body);
      void fetchList();

      return { ok: true, message: String((data as { message?: string })?.message ?? '') };
    } catch (e: unknown) {
      return failure(e);
    }
  };
  /** AB#22625 AC10 — a development being scoped becomes a product with several styles (or stops). */
  const makeProduct = (id: number, isProduct: boolean, rule?: ReleaseRule) =>
    write(() =>
      http.put(`product-development/${id}/product`, {
        is_product: isProduct,
        ...(rule ? { release_rule: rule } : {}),
      })
    );
  /** AB#22625 AC11, AC13 — a style added to a product: a sub-development (not AB#22122's extra code). */
  const addSubStyle = (
    id: number,
    body: { title: string; style_code?: string | null; shares_pattern_of_id?: number | null }
  ) => write(() => http.post(`product-development/${id}/subs`, body));
  /** AB#22625 AC15 — a loose development moved under a product. */
  const adoptStyle = (id: number, developmentId: number) =>
    write(() =>
      http.post(`product-development/${id}/subs/adopt`, { development_id: developmentId })
    );
  /** AB#22814 AC13 — a style split back out into a development of its own. */
  const splitStyle = (id: number) => write(() => http.post(`product-development/${id}/split`));
  /** AB#22625 AC16 — the release rule. */
  const setReleaseRule = (id: number, rule: ReleaseRule) =>
    write(() => http.put(`product-development/${id}/release-rule`, { release_rule: rule }));
  /** AB#22814 AC4–AC11 — what the merge would do; nothing is written. */
  const mergePreview = async (
    body: MergeBody
  ): Promise<{ ok: boolean; message: string; preview?: MergePreview }> => {
    try {
      const { data } = await http.post('product-development/merge/preview', body);

      return { ok: true, message: '', preview: result<MergePreview>(data) ?? undefined };
    } catch (e: unknown) {
      return failure(e);
    }
  };
  /** AB#22814 — the merge, as previewed, with the style names as edited. */
  const merge = (body: MergeBody & { names: Record<number, string> }) =>
    write(() => http.post('product-development/merge', body));
  /** AB#22814 AC16, AC17 — possible products, and dismissing one for good. */
  const mergeSuggestions = async (): Promise<MergeSuggestion[]> => {
    try {
      const { data } = await http.get('product-development/merge/suggestions');

      return result<{ suggestions: MergeSuggestion[] }>(data)?.suggestions ?? [];
    } catch {
      return [];
    }
  };
  const dismissSuggestion = async (key: string): Promise<WriteResult> => {
    try {
      await http.post('product-development/merge/suggestions/dismiss', { key });

      return { ok: true, message: 'Dismissed.' };
    } catch (e: unknown) {
      return failure(e);
    }
  };
  /** AB#22813 AC19 — a development moved in the shared priority order: before another, or last. */
  const moveInOrder = async (id: number, beforeId: number | null): Promise<WriteResult> => {
    try {
      await http.post(`product-development/${id}/order`, { before_id: beforeId });
      await fetchList();

      return { ok: true, message: '' };
    } catch (e: unknown) {
      return failure(e);
    }
  };
  const failure = (e: unknown): WriteResult => {
    const r = (
      e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
    )?.response?.data;

    return {
      ok: false,
      message:
        (r?.errors ? Object.values(r.errors)[0]?.[0] : undefined) ||
        r?.message ||
        'That did not work.',
    };
  };

  const fetchList = async (over: { page?: number; per_page?: number } = {}): Promise<void> => {
    if (over.page) page.value = over.page;
    if (over.per_page) perPage.value = over.per_page;
    fetching.value = true;
    if (!items.value.length) loading.value = true;
    try {
      const params: Record<string, string | number> = {
        chip: chip.value,
        page: page.value,
        per_page: perPage.value,
      };
      if (q.value.trim()) params.q = q.value.trim();
      if (ownerId.value) params.owner_id = ownerId.value;
      if (companyId.value) params.company_id = companyId.value;
      if (sort.value.key !== 'priority') {
        params.sort = sort.value.key;
        params.dir = sort.value.dir;
      }
      const { data } = await http.get('product-development', { params });
      const r = result<{
        items: DevelopmentRow[];
        total: number;
        last_page: number;
        counts: Counts;
      }>(data);
      items.value = r?.items ?? [];
      total.value = Number(r?.total ?? 0);
      lastPage.value = Number(r?.last_page ?? 1);
      counts.value = r?.counts ?? null;
    } catch (e) {
      handleResponseException(e);
    } finally {
      fetching.value = false;
      loading.value = false;
    }
  };

  const fetchOptions = async (): Promise<void> => {
    try {
      const { data } = await http.get('product-development/options');
      options.value = result<DevelopmentOptions>(data) ?? null;
    } catch (e) {
      handleResponseException(e);
    }
  };

  const open = async (id: number): Promise<void> => {
    currentLoading.value = true;
    try {
      const { data } = await http.get(`product-development/${id}`);
      current.value = result<{ development: Development }>(data)?.development ?? null;
    } catch (e) {
      handleResponseException(e);
    } finally {
      currentLoading.value = false;
    }
  };

  const close = (): void => {
    current.value = null;
  };

  /** A write that answers with the whole record: keeps the drawer in step with the server. */
  const write = async (fn: () => Promise<{ data: unknown }>): Promise<WriteResult> => {
    busy.value = true;
    try {
      const { data } = await fn();
      const dev = result<{ development?: Development }>(data)?.development;
      if (dev) current.value = dev;
      void fetchList();

      return {
        ok: (data as { success?: boolean })?.success !== false,
        message: String((data as { message?: string })?.message ?? ''),
        development: dev,
      };
    } catch (e: unknown) {
      const r = (
        e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      )?.response?.data;
      const first = r?.errors ? Object.values(r.errors)[0]?.[0] : undefined;

      return { ok: false, message: first || r?.message || 'That did not work.' };
    } finally {
      busy.value = false;
    }
  };

  const createRequest = (body: Record<string, unknown>) =>
    write(() => http.post('product-development', body));
  const scope = (id: number, body: Record<string, unknown>) =>
    write(() => http.put(`product-development/${id}/scope`, body));
  const kickOff = (id: number) => write(() => http.post(`product-development/${id}/kick-off`));
  const cancel = (id: number, reason: string) =>
    write(() => http.post(`product-development/${id}/cancel`, { reason }));
  const tick = (id: number, step: number, index: number, ticked: boolean) =>
    write(() => http.post(`product-development/${id}/steps/${step}/tick`, { index, ticked }));
  const closeStep = (id: number, step: number) =>
    write(() => http.post(`product-development/${id}/steps/${step}/close`));
  const sendBack = (id: number, step: number, reason: string) =>
    write(() => http.post(`product-development/${id}/steps/${step}/send-back`, { reason }));
  // Phase 2 — the gates and the sample rounds.
  /** AB#22627 AC6–AC12 — with what it was approved on (and, visually, why). */
  const approvePattern = (
    id: number,
    note: string | null,
    evidence?: { evidence: 'physical' | 'visual'; evidence_note: string | null }
  ) =>
    write(() =>
      http.post(`product-development/${id}/gates/pattern`, { note, ...(evidence ?? {}) })
    );
  /** AB#22627 AC19, AC20 — a contribution given to someone, or marked not needed with a reason. */
  const setContribution = (id: number, step: number, key: string, body: Record<string, unknown>) =>
    write(() => http.put(`product-development/${id}/steps/${step}/contributions/${key}`, body));
  /** AB#22624 AC17 — the 3D owner's answer to a pattern that changed: redo or keep. */
  const answerPatternChanged = (id: number, answer: 'redo' | 'keep', note: string | null) =>
    write(() => http.post(`product-development/${id}/pattern-changed`, { answer, note }));
  /** AB#22222 — the pattern maker rejects the pattern: the sample goes for another round. */
  const rejectPattern = (id: number, reason: string) =>
    write(() => http.post(`product-development/${id}/gates/pattern/reject`, { reason }));
  /** AB#22315 — go-live not approved: the configuration opens again, with why. */
  const rejectGoLive = (id: number, reason: string) =>
    write(() => http.post(`product-development/${id}/gates/go-live/reject`, { reason }));
  const goLive = (
    id: number,
    note: string | null,
    productId?: number | null,
    withoutSizeRun?: string | null,
    launch?: { productIds: number[]; activeFrom: string | null }
  ) =>
    write(() =>
      http.post(`product-development/${id}/gates/go-live`, {
        note,
        ...(productId ? { product_id: productId } : {}),
        // AB#22310 — the products it launches for, and from when where it replaces another code.
        ...(launch?.productIds.length
          ? { product_ids: launch.productIds, active_from: launch.activeFrom || null }
          : {}),
        // AB#22224 — the PD lead decides to go live without the size run, and why.
        ...(withoutSizeRun ? { without_size_run: withoutSizeRun } : {}),
      })
    );
  /** AB#22303 — everything that happened to the development, newest first. */
  const activity = async (id: number): Promise<ActivityItem[]> => {
    try {
      const { data } = await http.get(`product-development/${id}/activity`);

      return result<{ items: ActivityItem[] }>(data)?.items ?? [];
    } catch (e) {
      handleResponseException(e);

      return [];
    }
  };
  const addSample = (id: number, body: Record<string, unknown>) =>
    write(() => http.post(`product-development/${id}/samples`, body));
  const updateSample = (id: number, sampleId: number, body: Record<string, unknown>) =>
    write(() => http.put(`product-development/${id}/samples/${sampleId}`, body));
  /** AB#22375 — the customer's answer on a prototype round, recorded by the account holder. */
  const customerSample = (id: number, sampleId: number, body: Record<string, unknown>) =>
    write(() => http.post(`product-development/${id}/samples/${sampleId}/customer`, body));
  /** AB#22387 AC12 — a requested round called off, with a reason. */
  const cancelSample = (id: number, sampleId: number, reason: string) =>
    write(() => http.post(`product-development/${id}/samples/${sampleId}/cancel`, { reason }));
  // AB#22122 — the other styles of a development.
  const addStyle = (id: number, body: Record<string, unknown>) =>
    write(() => http.post(`product-development/${id}/styles`, body));
  const updateStyle = (id: number, styleId: number, body: Record<string, unknown>) =>
    write(() => http.put(`product-development/${id}/styles/${styleId}`, body));
  const removeStyle = (id: number, styleId: number) =>
    write(() => http.delete(`product-development/${id}/styles/${styleId}`));
  // AB#22120 — "Skip physical sample" (step 3), with a reason.
  const skipStep = (id: number, step: number, reason: string) =>
    write(() => http.post(`product-development/${id}/steps/${step}/skip`, { reason }));
  /** AB#22530 AC16 — an imported development checked by the PD lead: the flag comes off. */
  const markReviewed = async (id: number): Promise<WriteResult> => {
    const r = await write(() => http.post(`product-development/${id}/reviewed`));
    if (r.ok) await open(id);

    return r;
  };
  /** AB#22530 — an imported development's style code, set or changed until it is in the style code list. */
  const setImportedCode = async (id: number, code: string | null): Promise<WriteResult> => {
    const r = await write(() => http.put(`product-development/${id}/scope`, { style_code: code }));
    if (r.ok) await open(id);

    return r;
  };
  /** AB#22388 — codes already in the list, by their first characters or a product's name. */
  const lookupStyleCode = async (q: string): Promise<ExistingStyleCode[]> => {
    try {
      const { data } = await http.get('product-development/style-codes/lookup', { params: { q } });

      return (data as { result?: { items?: ExistingStyleCode[] } })?.result?.items ?? [];
    } catch {
      return [];
    }
  };
  const previewStyleCode = async (q: {
    category: string;
    type: string;
    age: string;
    year?: number | null;
    number?: number | null;
    development_id?: number | null;
  }): Promise<StyleCodePreview | null> => {
    try {
      const params: Record<string, string | number> = {
        category: q.category,
        type: q.type,
        age: q.age,
      };
      if (q.year) params.year = q.year;
      if (q.number) params.number = q.number;
      if (q.development_id) params.development_id = q.development_id;
      const { data } = await http.get('product-development/style-codes/preview', { params });

      return result<StyleCodePreview>(data) ?? null;
    } catch {
      return null;
    }
  };
  const setOwner = (
    id: number,
    step: number,
    owner_id: number | null,
    co_owner_id: number | null
  ) =>
    write(() =>
      http.put(`product-development/${id}/steps/${step}/owner`, { owner_id, co_owner_id })
    );

  /**
   * Upload: open it (the backend names it and gives a presigned PUT), put the
   * bytes, confirm. Nothing is a file until the confirm.
   * AB#22225 — `replace`: a TDS that replaces the one attached (never two).
   * AB#22629 — `opts`: what to do with a file of the same name in the folder
   * (keep both, the default, or replace it), or the file this one replaces.
   */
  const upload = async (
    id: number,
    target: {
      kind: string;
      label?: string;
      style_id?: number;
      size?: string;
      sample_id?: number;
      step?: number;
      folder_id?: number;
    },
    file: File,
    note: string,
    replace = false,
    opts: {
      onCollision?: 'keep_both' | 'replace';
      replaceVersionId?: number;
      /** AB#22634 AC5 — asked whether the type is right for an unusual extension, and said yes. */
      typeConfirmed?: boolean;
      /** AB#22637 — a new version of this file, with its level and answer (the note is the upload's note). */
      versionOf?: number;
      revision?: string;
      patternInstruction?: PatternInstruction;
    } & UploadTracking = {}
  ): Promise<WriteResult> => {
    busy.value = true;
    try {
      const meta = {
        original_name: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        // AB#22225 — a note is optional.
        comment: note.trim() || null,
        ...(replace ? { replace: true } : {}),
        ...(opts.onCollision ? { on_collision: opts.onCollision } : {}),
        ...(opts.replaceVersionId ? { replace_version_id: opts.replaceVersionId } : {}),
        ...(opts.typeConfirmed ? { type_confirmed: true } : {}),
        ...(opts.versionOf
          ? {
              version_of: opts.versionOf,
              revision: opts.revision?.trim() || null,
              pattern_instruction: opts.patternInstruction,
            }
          : {}),
      };
      const { data } = await http.post(`product-development/${id}/files`, { ...target, ...meta });
      const r = result<{ version_id: number; upload_url: string; renamed?: boolean }>(data);
      // AB#22629 AC4 — kept beside a file of the same name: the name it was given is said.
      const kept = r?.renamed ? String((data as { message?: string })?.message ?? '') : '';
      if (!r?.upload_url) return { ok: false, message: 'The upload could not be prepared.' };
      const put = await putFile(r.upload_url, file, meta.content_type, opts);
      if (!put.ok) {
        // AB#22629 AC11 — nothing half-uploaded stays behind.
        void http
          .delete(`product-development/${id}/versions/${r.version_id}/pending`)
          .catch(() => undefined);

        return put.aborted
          ? { ok: false, cancelled: true, message: `${file.name}: cancelled, nothing was kept.` }
          : { ok: false, message: `${file.name} was not uploaded. ${uploadFailure(put.status)}` };
      }
      // Phase 2b — the names inside an archive, for the upload checks.
      const zip_entries = /\.zip$/i.test(file.name) ? await readZipEntries(file) : null;

      const done = await write(() =>
        http.post(`product-development/${id}/versions/${r.version_id}/confirm`, {
          size_bytes: file.size,
          ...(zip_entries ? { zip_entries } : {}),
        })
      );

      return kept ? { ...done, message: `${kept} ${done.message}`.trim() } : done;
    } catch (e: unknown) {
      const r = (
        e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      )?.response?.data;
      const first = r?.errors ? Object.values(r.errors)[0]?.[0] : undefined;

      return { ok: false, message: first || r?.message || 'The upload failed.' };
    } finally {
      busy.value = false;
    }
  };

  // Phase 2b — a name on the upload warnings; a comment, with the people it mentions.
  const acknowledge = (id: number, fileId: number) =>
    write(() => http.post(`product-development/${id}/versions/${fileId}/acknowledge`));
  /** AB#22317 — a flagged file confirmed as the right one, with the name of whoever confirmed it. */
  const confirmName = (id: number, fileId: number) =>
    write(() => http.post(`product-development/${id}/versions/${fileId}/confirm-name`));
  /** AB#22218 — the development's owner and secondary owner. */
  const setOwners = (id: number, ownerId: number | null, secondaryId: number | null) =>
    write(() =>
      http.put(`product-development/${id}/owners`, {
        owner_id: ownerId,
        secondary_owner_id: secondaryId,
      })
    );
  /** AB#22368 — folders, moves and a file's type. */
  const createFolder = (id: number, name: string, parentId: number | null) =>
    write(() => http.post(`product-development/${id}/folders`, { name, parent_id: parentId }));
  const updateFolder = (
    id: number,
    folderId: number,
    body: { name?: string; parent_id?: number | null }
  ) => write(() => http.put(`product-development/${id}/folders/${folderId}`, body));
  const deleteFolder = (id: number, folderId: number, confirm: boolean) =>
    write(() =>
      http.delete(`product-development/${id}/folders/${folderId}`, {
        data: confirm ? { confirm: true } : {},
      })
    );
  const moveFiles = (id: number, versionIds: number[], folderIds: number[], to: number | null) =>
    write(() =>
      http.post(`product-development/${id}/files/move`, {
        version_ids: versionIds,
        folder_ids: folderIds,
        to_folder_id: to,
      })
    );
  const changeFileKind = (id: number, versionId: number, kind: string) =>
    write(() => http.put(`product-development/${id}/versions/${versionId}/kind`, { kind }));
  /** AB#22373 — a step's date set by hand (null: planned again from the production date). */
  const setStepDue = (id: number, step: number, dueAt: string | null) =>
    write(() => http.put(`product-development/${id}/steps/${step}/due`, { due_at: dueAt }));
  /** AB#22225 — a file leaves the Files section (it stays in the activity). */
  const deleteFile = (id: number, fileId: number) =>
    write(() => http.delete(`product-development/${id}/files/${fileId}`));
  /** AB#22629 — a deleted or replaced file back in the Files section. */
  const restoreFile = (id: number, versionId: number) =>
    write(() => http.post(`product-development/${id}/versions/${versionId}/restore`));
  const addComment = (
    id: number,
    body: string,
    step: number | null,
    version_id: number | null,
    mentions: number[],
    parentId?: number | null
  ) =>
    write(() =>
      http.post(`product-development/${id}/comments`, {
        body,
        step,
        version_id,
        mentions,
        ...(parentId ? { parent_id: parentId } : {}),
      })
    );
  // AB#22770 AC5 — a link kept as a reference.
  const addLink = (id: number, url: string, label: string | null) =>
    write(() => http.post(`product-development/${id}/links`, { url, label }));
  const removeLink = (id: number, linkId: number) =>
    write(() => http.delete(`product-development/${id}/links/${linkId}`));
  // AB#22639 — a comment changed or taken back by its author; its files; read; searched.
  const editComment = (id: number, commentId: number, body: string) =>
    write(() => http.put(`product-development/${id}/comments/${commentId}`, { body }));
  const deleteComment = (id: number, commentId: number) =>
    write(() => http.delete(`product-development/${id}/comments/${commentId}`));
  const attachToComment = async (
    id: number,
    commentId: number,
    file: File
  ): Promise<WriteResult> => {
    try {
      const { data } = await http.post(`product-development/${id}/comments/${commentId}/files`, {
        original_name: file.name,
        content_type: file.type || null,
        size_bytes: file.size,
      });
      const opened = result<{ id: number; upload_url: string | null }>(data);
      if (!opened) return { ok: false, message: 'The file could not be attached.' };
      if (opened.upload_url) {
        const put = await putFile(opened.upload_url, file, file.type || 'application/octet-stream');
        if (!put.ok) return { ok: false, message: `${file.name} did not upload. Try again.` };
      }

      return write(() =>
        http.post(`product-development/${id}/comments/${commentId}/files/${opened.id}/confirm`)
      );
    } catch {
      return { ok: false, message: `${file.name} could not be attached.` };
    }
  };
  const downloadCommentFile = async (
    id: number,
    commentId: number,
    fileId: number
  ): Promise<void> => {
    try {
      const { data } = await http.get(
        `product-development/${id}/comments/${commentId}/files/${fileId}`
      );
      const url = result<{ url: string | null }>(data)?.url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      handleResponseException(e);
    }
  };
  const fileCommentAttachment = (id: number, commentId: number, fileId: number, kind: string) =>
    write(() =>
      http.post(`product-development/${id}/comments/${commentId}/files/${fileId}/to-development`, {
        kind,
      })
    );
  const readComments = async (id: number): Promise<void> => {
    try {
      await http.post(`product-development/${id}/comments/read`);
      if (current.value?.id === id) current.value = { ...current.value, unread_comments: 0 };
    } catch {
      /* the count catches up next time */
    }
  };
  const searchComments = async (q: string): Promise<FoundComment[]> => {
    try {
      const { data } = await http.get('product-development/comments/search', { params: { q } });

      return result<{ comments: FoundComment[] }>(data)?.comments ?? [];
    } catch {
      return [];
    }
  };

  /**
   * AB#22632 — a package: made (with its ZIP when it came as one), then each
   * file put into it. Files that fail are named; the rest are kept.
   */
  const uploadPackage = async (
    id: number,
    spec: {
      kind: string;
      name: string;
      folder_id: number | null;
      archive: File | null;
      /** AB#22637 AC5 — a new version of this package, with its answer. */
      versionOf?: { of: number } & RevisionAnswer;
    },
    entries: PackageEntry[],
    typeConfirmed = false,
    t: UploadTracking = {}
  ): Promise<WriteResult> => {
    busy.value = true;
    const failed: string[] = [];
    // AB#22629 AC9 — one bar for the package: its bytes so far of all of them.
    const total = (spec.archive?.size ?? 0) + entries.reduce((n, e) => n + e.file.size, 0);
    let sent = 0;
    const part = (loaded: number): void => t.onProgress?.(sent + loaded, total);
    try {
      const { data } = await http.post(`product-development/${id}/packages`, {
        kind: spec.kind,
        name: spec.name,
        folder_id: spec.folder_id,
        ...(spec.versionOf
          ? {
              version_of: spec.versionOf.of,
              revision: spec.versionOf.revision.trim() || null,
              pattern_instruction: spec.versionOf.instruction,
              note: spec.versionOf.note,
            }
          : {}),
        ...(spec.archive
          ? {
              archive: {
                name: spec.archive.name,
                size_bytes: spec.archive.size,
                content_type: spec.archive.type || 'application/zip',
              },
            }
          : {}),
      });
      const made = result<{ package_id: number; archive_upload_url: string | null }>(data);
      if (!made?.package_id) return { ok: false, message: 'The package could not be made.' };
      if (spec.archive && made.archive_upload_url) {
        const put = await putFile(
          made.archive_upload_url,
          spec.archive,
          spec.archive.type || 'application/zip',
          { onProgress: part, signal: t.signal }
        );
        sent += spec.archive.size;
        if (put.ok)
          await http.post(`product-development/${id}/packages/${made.package_id}/archive`);
        else failed.push(spec.archive.name);
      }
      for (const e of entries) {
        if (t.signal?.aborted) {
          failed.push(e.path ? `${e.path}/${e.file.name}` : e.file.name);
          continue;
        }
        try {
          const open = await http.post(`product-development/${id}/files`, {
            kind: spec.kind,
            package_id: made.package_id,
            relative_path: e.path,
            age_group: e.age || null,
            original_name: e.file.name,
            content_type: e.file.type || 'application/octet-stream',
            size_bytes: e.file.size,
            ...(typeConfirmed ? { type_confirmed: true } : {}),
          });
          const r = result<{ version_id: number; upload_url: string }>(open.data);
          const put = await putFile(
            r.upload_url,
            e.file,
            e.file.type || 'application/octet-stream',
            {
              onProgress: part,
              signal: t.signal,
            }
          );
          sent += e.file.size;
          if (!put.ok) {
            void http
              .delete(`product-development/${id}/versions/${r.version_id}/pending`)
              .catch(() => undefined);
            throw new Error(String(put.status));
          }
          await http.post(`product-development/${id}/versions/${r.version_id}/confirm`, {
            size_bytes: e.file.size,
          });
        } catch {
          failed.push(e.path ? `${e.path}/${e.file.name}` : e.file.name);
        }
      }
    } catch (e: unknown) {
      const r = (
        e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      )?.response?.data;

      return {
        ok: false,
        message:
          (r?.errors ? Object.values(r.errors)[0]?.[0] : undefined) ||
          r?.message ||
          'The package could not be made.',
      };
    } finally {
      busy.value = false;
    }
    const done = await write(() => http.get(`product-development/${id}`));
    const n = entries.length - failed.filter((f) => f !== spec.archive?.name).length;

    return {
      ...done,
      ok: failed.length === 0,
      message: failed.length
        ? `${spec.name}: ${n} of ${entries.length} files in; not stored: ${failed.join(', ')}.`
        : `${spec.name}: ${n} file${n === 1 ? '' : 's'} in one package.`,
    };
  };
  /** AB#22637 AC3 — every version of a file, oldest first. */
  const fileHistory = async (id: number, versionId: number): Promise<FileVersionEntry[]> => {
    try {
      const { data } = await http.get(`product-development/${id}/versions/${versionId}/history`);

      return result<{ versions: FileVersionEntry[] }>(data)?.versions ?? [];
    } catch {
      return [];
    }
  };
  /** AB#22637 AC8 — the pattern adjustment asked for was made. */
  const patternAdjusted = (id: number, adjustmentId: number, note: string | null) =>
    write(() =>
      http.post(`product-development/${id}/pattern-adjustments/${adjustmentId}/done`, { note })
    );
  const updatePackage = (
    id: number,
    packageId: number,
    body: { name?: string; folder_id?: number | null }
  ) => write(() => http.put(`product-development/${id}/packages/${packageId}`, body));
  const deletePackage = (id: number, packageId: number) =>
    write(() => http.delete(`product-development/${id}/packages/${packageId}`));
  const addToPackage = (id: number, packageId: number, versionIds: number[]) =>
    write(() =>
      http.post(`product-development/${id}/packages/${packageId}/files`, {
        version_ids: versionIds,
      })
    );
  const takeOutOfPackage = (id: number, packageId: number, versionId: number) =>
    write(() => http.delete(`product-development/${id}/packages/${packageId}/files/${versionId}`));
  const setAgeGroup = (id: number, versionId: number, ageGroup: string | null) =>
    write(() =>
      http.put(`product-development/${id}/versions/${versionId}/age-group`, { age_group: ageGroup })
    );
  /**
   * AB#22632 AC16 — the whole package: its ZIP as it came, or one made here
   * from its files.
   */
  const downloadPackage = async (id: number, pkg: DevelopmentPackage): Promise<WriteResult> => {
    try {
      const { data } = await http.get(`product-development/${id}/packages/${pkg.id}/download`);
      const url = result<{ url: string | null }>(data)?.url;
      if (url) {
        window.open(url, '_blank', 'noopener');

        return { ok: true, message: '' };
      }
      const files = (current.value?.files ?? []).filter((f) => f.package_id === pkg.id);
      const zip = new JSZip();
      for (const f of files) {
        const r = await http.get(`product-development/${id}/versions/${f.id}/download`);
        const fileUrl = result<{ url: string }>(r.data)?.url;
        if (!fileUrl) continue;
        const body = await fetch(fileUrl);
        zip.file(f.relative_path ? `${f.relative_path}/${f.name}` : f.name, await body.blob());
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${pkg.name}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);

      return { ok: true, message: '' };
    } catch {
      return {
        ok: false,
        message: 'The package could not be put together here: download its files one by one.',
      };
    }
  };

  /**
   * AB#22636 — a pack (the factory pack) put together now: the backend names
   * what is missing or gives the files, and the ZIP is made here, with the
   * note for the factory beside them.
   */
  const downloadPack = async (
    id: number,
    pack: string,
    body: Record<string, unknown> = {}
  ): Promise<WriteResult> => {
    let built: { name: string; files: { path: string; url: string }[]; notes: string | null };
    try {
      const { data } = await http.post(`product-development/${id}/packs/${pack}`, body);
      built = result<typeof built>(data) as typeof built;
    } catch (e: unknown) {
      const r = (
        e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
      )?.response?.data;

      return {
        ok: false,
        message:
          (r?.errors ? Object.values(r.errors)[0]?.[0] : undefined) ||
          r?.message ||
          'The pack could not be put together.',
      };
    }
    try {
      const zip = new JSZip();
      for (const f of built.files) zip.file(f.path, await (await fetch(f.url)).blob());
      if (built.notes) zip.file('README.txt', built.notes);
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${built.name}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      void open(id);

      return { ok: true, message: '' };
    } catch {
      return {
        ok: false,
        message: 'The pack could not be put together here: download its files one by one.',
      };
    }
  };

  const download = async (id: number, versionId: number): Promise<void> => {
    try {
      const { data } = await http.get(`product-development/${id}/versions/${versionId}/download`);
      const url = result<{ url: string }>(data)?.url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (e) {
      handleResponseException(e);
    }
  };

  return {
    items,
    total,
    page,
    perPage,
    lastPage,
    counts,
    chip,
    q,
    ownerId,
    companyId,
    loading,
    fetching,
    options,
    current,
    currentLoading,
    busy,
    fetchList,
    fetchOptions,
    open,
    close,
    createRequest,
    scope,
    kickOff,
    cancel,
    tick,
    closeStep,
    sendBack,
    setOwner,
    skipStep,
    previewStyleCode,
    lookupStyleCode,
    markReviewed,
    setImportedCode,
    addStyle,
    updateStyle,
    removeStyle,
    approvePattern,
    setContribution,
    answerPatternChanged,
    rejectPattern,
    rejectGoLive,
    goLive,
    activity,
    addSample,
    updateSample,
    upload,
    download,
    acknowledge,
    confirmName,
    deleteFile,
    restoreFile,
    fileHistory,
    patternAdjusted,
    uploadPackage,
    updatePackage,
    deletePackage,
    addToPackage,
    takeOutOfPackage,
    setAgeGroup,
    downloadPackage,
    sort,
    makeProduct,
    addSubStyle,
    adoptStyle,
    splitStyle,
    setReleaseRule,
    mergePreview,
    merge,
    mergeSuggestions,
    dismissSuggestion,
    filteredIds,
    editField,
    bulkAction,
    moveInOrder,
    downloadPack,
    addLink,
    removeLink,
    setOwners,
    setStepDue,
    customerSample,
    cancelSample,
    createFolder,
    updateFolder,
    deleteFolder,
    moveFiles,
    changeFileKind,
    addComment,
    editComment,
    deleteComment,
    attachToComment,
    downloadCommentFile,
    fileCommentAttachment,
    readComments,
    searchComments,
  };
}

/**
 * Phase 2b — the file names inside a .zip, from its central directory (the
 * last part of the file), without unpacking anything. Null when the archive
 * cannot be read as one; the backend then skips the archive checks.
 */
/**
 * AB#22632 AC10, AC13 — the files inside a ZIP, with the folders they sit
 * in; null when it cannot be read. Folders and system files are left out.
 */
export const unzip = async (file: File): Promise<{ file: File; path: string | null }[] | null> => {
  try {
    const zip = await JSZip.loadAsync(file);
    const out: { file: File; path: string | null }[] = [];
    for (const e of Object.values(zip.files)) {
      if (e.dir || /(^|\/)(__MACOSX|\.DS_Store)(\/|$)/.test(e.name)) continue;
      const parts = e.name.split('/');
      const name = parts.pop() ?? e.name;
      out.push({
        file: new File([await e.async('blob')], name),
        path: parts.length ? parts.join('/') : null,
      });
    }

    return out;
  } catch {
    return null;
  }
};

/** AB#22632 AC12 — the age group a name says, among the style's: adult, women, youth or their short forms. */
export const guessAgeGroup = (name: string, groups: string[]): string => {
  const words = name
    .replace(/\.[^.]+$/, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/);
  const says: Record<string, string[]> = {
    adult: ['adult', 'adults', 'ad', 'men', 'mens', 'man', 'senior', 'sr'],
    women: ['women', 'womens', 'woman', 'ladies', 'lady', 'wm', 'wmn', 'w'],
    youth: ['youth', 'yt', 'y', 'junior', 'jr', 'kids'],
    girl: ['girl', 'girls', 'gi'],
    kid: ['kid', 'child', 'children'],
    unisex: ['unisex', 'us'],
  };
  const hit = groups.filter((g) => (says[g] ?? [g]).some((w) => words.includes(w)));

  return hit.length === 1 ? hit[0] : '';
};

/**
 * AB#22632 AC11 — what was dropped: loose files, and each folder as its
 * files with the folders inside it kept as paths.
 */
export const readDropped = async (
  dt: DataTransfer
): Promise<{
  files: File[];
  folders: { name: string; entries: { file: File; path: string | null }[] }[];
}> => {
  const roots = Array.from(dt.items ?? [])
    .map((i) => i.webkitGetAsEntry?.() ?? null)
    .filter((e): e is FileSystemEntry => !!e);
  if (!roots.some((e) => e.isDirectory)) return { files: Array.from(dt.files ?? []), folders: [] };
  const fileOf = (e: FileSystemEntry): Promise<File> =>
    new Promise((ok, err) => (e as FileSystemFileEntry).file(ok, err));
  const children = async (e: FileSystemEntry): Promise<FileSystemEntry[]> => {
    const reader = (e as FileSystemDirectoryEntry).createReader();
    const all: FileSystemEntry[] = [];
    // readEntries answers in batches until it answers with none.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((ok, err) => reader.readEntries(ok, err));
      if (!batch.length) return all;
      all.push(...batch);
    }
  };
  const walk = async (
    e: FileSystemEntry,
    path: string | null,
    out: { file: File; path: string | null }[]
  ): Promise<void> => {
    if (e.isFile) {
      if (e.name !== '.DS_Store') out.push({ file: await fileOf(e), path });

      return;
    }
    for (const c of await children(e)) await walk(c, path ? `${path}/${e.name}` : e.name, out);
  };
  const files: File[] = [];
  const folders: { name: string; entries: { file: File; path: string | null }[] }[] = [];
  for (const r of roots) {
    if (r.isFile) {
      files.push(await fileOf(r));
      continue;
    }
    const entries: { file: File; path: string | null }[] = [];
    for (const c of await children(r)) await walk(c, null, entries);
    folders.push({ name: r.name, entries });
  }

  return { files, folders };
};

export const readZipEntries = async (file: File): Promise<string[] | null> => {
  // Blob.arrayBuffer() where the browser has it; FileReader where it does not.
  const bytes = (b: Blob): Promise<ArrayBuffer> =>
    typeof b.arrayBuffer === 'function'
      ? b.arrayBuffer()
      : new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as ArrayBuffer);
          r.onerror = () => reject(r.error);
          r.readAsArrayBuffer(b);
        });
  try {
    const tailSize = Math.min(file.size, 65557);
    const tail = new DataView(await bytes(file.slice(file.size - tailSize)));
    let eocd = -1;
    for (let i = tail.byteLength - 22; i >= 0; i--) {
      if (tail.getUint32(i, true) === 0x06054b50) {
        eocd = i;
        break;
      }
    }
    if (eocd < 0) return null;
    const count = tail.getUint16(eocd + 10, true);
    const cdSize = tail.getUint32(eocd + 12, true);
    const cdOffset = tail.getUint32(eocd + 16, true);
    if (cdOffset + cdSize > file.size) return null;
    const cd = new DataView(await bytes(file.slice(cdOffset, cdOffset + cdSize)));
    const names: string[] = [];
    let p = 0;
    const decoder = new TextDecoder('utf-8');
    for (let n = 0; n < count && p + 46 <= cd.byteLength; n++) {
      if (cd.getUint32(p, true) !== 0x02014b50) break;
      const nameLen = cd.getUint16(p + 28, true);
      const extraLen = cd.getUint16(p + 30, true);
      const commentLen = cd.getUint16(p + 32, true);
      names.push(decoder.decode(new Uint8Array(cd.buffer, p + 46, nameLen)));
      p += 46 + nameLen + extraLen + commentLen;
      if (names.length >= 2000) break;
    }

    return names;
  } catch {
    return null;
  }
};

export interface DevelopmentSettings {
  steps: { step: number; key: string; label: string }[];
  routes: { key: string; label: string; skips: number[] }[];
  checklists: Record<string, string[]>;
  days: Record<string, Record<string, number>>;
  /** Phase 2b — templates per type code (only what each template overrides). */
  kinds?: string[];
  checklists_by_kind?: Record<string, Record<string, string[]>>;
  /** AB#22635 — the tags an item can carry (route, production method, age group), and the version the checklists are on. */
  checklist_tags?: { tag: string; kind: 'route' | 'method' | 'age' }[];
  checklists_version?: number;
  /** AB#22636 — age group => the size a standard prototype is made in, and the age groups. */
  standard_sample_sizes?: Record<string, string>;
  age_groups?: { value: string; label: string }[];
  /** AB#22770 — per route: step => the file types it requires (only what Setup set). */
  route_files?: Record<string, Record<string, string[]>>;
  days_by_kind?: Record<string, Record<string, Record<string, number>>>;
  /** AB#22120 */
  default_owners?: Record<string, number>;
  /** AB#22634 — the file types, as Setup keeps them. */
  file_types?: FileType[];
  /** AB#22633 — the methods, the general files per step, and per method its files, factory set and days. */
  production_methods?: { key: string; label: string }[];
  step_files?: Record<string, string[]>;
  method_files?: Record<string, MethodFiles>;
  days_by_method?: Record<string, Record<string, Record<string, number>>>;
  code_lists?: CodeLists;
  style_codes?: { total: number; issued: number; imported: number; last_import: string | null };
  people?: Person[];
  /** AB#22870 — whether the board shows, and who switched it last. */
  board?: { on: boolean; by: string | null; at: string | null };
  updated: string | null;
}

/** AB#22634 — a file type: what it is called, the extensions it expects, retired or not. */
export interface FileType {
  key: string;
  label: string;
  extensions: string[];
  retired: boolean;
  /** The steps and gates are written against it: renamed, never retired. */
  built_in: boolean;
}

/** AB#22633 AC5, AC6 — a production method's files: per step (a step listed replaces the general list) and the factory's. */
export interface MethodFiles {
  steps: Record<string, string[]>;
  factory: string[];
}

/** AB#22633 AC10 — what does not say how it is produced yet. */
export interface UnansweredMethods {
  developments: {
    id: number;
    name: string;
    state: string;
    account: string;
    production_method_label: string;
  }[];
  requests: { id: number; name: string; status: string; account: string }[];
}

/** AB#22634 AC7 — a file typed Other, somewhere, waiting for a real type. */
export interface OtherFile {
  id: number;
  name: string;
  uploaded_at: string | null;
  uploaded_by: string | null;
  development_id: number;
  development: string;
}

/** AB#22120 — what the code-sheet import reports. */
export interface ImportReport {
  rows: number;
  added: number;
  known: number;
  skipped: number;
  unknown: string[];
}

export function useDevelopmentSettings() {
  const settings = ref<DevelopmentSettings | null>(null);
  const loading = ref(false);
  const saving = ref(false);

  const load = async (): Promise<void> => {
    loading.value = true;
    try {
      const { data } = await http.get('product-development/settings');
      settings.value = result<DevelopmentSettings>(data) ?? null;
    } catch (e) {
      handleResponseException(e);
    } finally {
      loading.value = false;
    }
  };

  const save = async (body: {
    checklists?: Record<string, string[]>;
    days?: Record<string, Record<string, number>>;
    checklists_by_kind?: Record<string, Record<string, string[]>>;
    days_by_kind?: Record<string, Record<string, Record<string, number>>>;
    default_owners?: Record<string, number | null>;
    file_types?: { key: string | null; label: string; extensions: string[]; retired: boolean }[];
    method_files?: Record<string, MethodFiles>;
    days_by_method?: Record<string, Record<string, Record<string, number>>>;
    standard_sample_sizes?: Record<string, string>;
    route_files?: Record<string, Record<string, string[]>>;
  }): Promise<{ ok: boolean; message: string }> => {
    saving.value = true;
    try {
      const { data } = await http.put('product-development/settings', body);
      settings.value = result<DevelopmentSettings>(data) ?? settings.value;

      return { ok: true, message: String((data as { message?: string })?.message ?? 'Saved.') };
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;

      return { ok: false, message: msg || 'The settings could not be saved.' };
    } finally {
      saving.value = false;
    }
  };

  const errorOf = (e: unknown, fallback: string): string => {
    const r = (
      e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }
    )?.response?.data;

    return (r?.errors ? Object.values(r.errors)[0]?.[0] : undefined) || r?.message || fallback;
  };

  /** AB#22634 AC7 — files typed Other, to be given a real type. */
  const otherFiles = ref<OtherFile[]>([]);
  const loadOtherFiles = async (): Promise<void> => {
    try {
      const { data } = await http.get('product-development/other-files');
      otherFiles.value = result<{ files: OtherFile[] }>(data)?.files ?? [];
    } catch {
      otherFiles.value = [];
    }
  };

  // AB#22633 AC10 — listed so the answer is filled in, never guessed.
  const unanswered = ref<UnansweredMethods>({ developments: [], requests: [] });
  const loadUnanswered = async (): Promise<void> => {
    try {
      const { data } = await http.get('product-development/unanswered-methods');
      unanswered.value = result<UnansweredMethods>(data) ?? { developments: [], requests: [] };
    } catch {
      unanswered.value = { developments: [], requests: [] };
    }
  };

  /** AB#22120 — add a code to a list, rename it or switch it off. */
  const saveCodeEntry = async (body: {
    list: keyof CodeLists;
    code: string;
    label: string;
    active?: boolean;
  }): Promise<{ ok: boolean; message: string }> => {
    saving.value = true;
    try {
      const { data } = await http.put('product-development/code-lists', body);
      settings.value = result<DevelopmentSettings>(data) ?? settings.value;

      return { ok: true, message: String((data as { message?: string })?.message ?? 'Saved.') };
    } catch (e) {
      return { ok: false, message: errorOf(e, 'The code could not be saved.') };
    } finally {
      saving.value = false;
    }
  };

  /** AB#22120 — Isabelle's code sheet: every code in it is recorded as taken. */
  const importCodes = async (
    file: File
  ): Promise<{ ok: boolean; message: string; report?: ImportReport }> => {
    saving.value = true;
    try {
      const form = new FormData();
      form.append('file', file);
      // The client defaults to JSON; a file must go as multipart or it arrives empty.
      const { data } = await http.post('product-development/style-codes/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const r = result<DevelopmentSettings & { report?: ImportReport }>(data);
      if (r) settings.value = r;

      return {
        ok: (data as { success?: boolean })?.success !== false,
        message: String((data as { message?: string })?.message ?? ''),
        report: r?.report,
      };
    } catch (e) {
      return { ok: false, message: errorOf(e, 'The sheet could not be imported.') };
    } finally {
      saving.value = false;
    }
  };

  // AB#22628 — the assignment rules, who each step may be given, what is left with someone gone.
  const assignment = ref<Assignment | null>(null);
  const loadAssignment = async (): Promise<void> => {
    try {
      const { data } = await http.get('product-development/assignment');
      const r = result<Assignment>(data);
      assignment.value = Array.isArray(r?.rules) ? r : null;
    } catch (e) {
      handleResponseException(e);
    }
  };
  const writeRule = async (
    call: () => Promise<{ data: unknown }>,
    fallback: string
  ): Promise<{ ok: boolean; message: string }> => {
    saving.value = true;
    try {
      const { data } = await call();
      assignment.value = result<Assignment>(data) ?? assignment.value;

      return { ok: true, message: 'Saved.' };
    } catch (e) {
      return { ok: false, message: errorOf(e, fallback) };
    } finally {
      saving.value = false;
    }
  };
  const addRule = (body: {
    step_key: string;
    match_type: AssignmentRule['match_type'];
    match_value: string;
    user_id: number;
  }) =>
    writeRule(
      () => http.post('product-development/assignment/rules', body),
      'The rule could not be saved.'
    );
  const removeRule = (id: number) =>
    writeRule(
      () => http.delete(`product-development/assignment/rules/${id}`),
      'The rule could not be removed.'
    );

  /** AB#22870 AC6 — the board shown or hidden: a setting, not a release. */
  const setBoardVisible = async (on: boolean): Promise<{ ok: boolean; message: string }> => {
    try {
      const { data } = await http.put('product-development/board/visibility', { on });
      settings.value = result<DevelopmentSettings>(data) ?? settings.value;

      return { ok: true, message: String((data as { message?: string })?.message ?? 'Saved.') };
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;

      return { ok: false, message: msg || 'The board could not be switched.' };
    }
  };

  return {
    settings,
    loading,
    saving,
    load,
    save,
    setBoardVisible,
    assignment,
    loadAssignment,
    addRule,
    removeRule,
    saveCodeEntry,
    importCodes,
    otherFiles,
    loadOtherFiles,
    unanswered,
    loadUnanswered,
  };
}

/* ------------------------------------------------------------------ insights */

/** Phase 2b — what GET product-development/insights answers. */
export interface InsightsSummary {
  kicked_off: number;
  went_live: number;
  cancelled: number;
  lead_days_median: number | null;
  build_days_median: number | null;
  steps_closed: number;
  steps_late: number;
  late_rate: number | null;
}

export interface InsightsStepRoute {
  n: number;
  median_days: number | null;
  p90_days: number | null;
  planned_days: number;
  late: number;
  late_rate: number | null;
}

export interface InsightsOwner {
  id: number;
  name: string;
  steps_done: number;
  late: number;
  late_rate: number | null;
  median_days: number | null;
  open_now: number;
}

export interface InsightsStall {
  development_id: number;
  style_code: string | null;
  title: string | null;
  company: string | null;
  step: number;
  label: string;
  owner: string | null;
  opened_at: string | null;
  last_activity_at: string;
  idle_days: number;
  planned_days: number;
  due_at: string | null;
  late: boolean;
}

export interface DevelopmentInsights {
  period: { key: string; from: string; to: string; label: string };
  previous: { from: string; to: string; label: string };
  summary: { now: InsightsSummary; previous: InsightsSummary };
  steps: { step: number; key: string; label: string; routes: Record<string, InsightsStepRoute> }[];
  owners: InsightsOwner[];
  stalls: InsightsStall[];
  months: { month: string; label: string; kicked_off: number; live: number }[];
}

export const INSIGHT_PERIODS: { key: string; label: string }[] = [
  { key: 'quarter', label: 'This quarter' },
  { key: '90d', label: 'Last 90 days' },
  { key: '180d', label: 'Last 180 days' },
  { key: 'year', label: 'Last 12 months' },
];

export function useDevelopmentInsights() {
  const insights = ref<DevelopmentInsights | null>(null);
  const period = ref('quarter');
  const loading = ref(false);

  const load = async (key?: string): Promise<void> => {
    if (key) period.value = key;
    loading.value = true;
    try {
      const { data } = await http.get('product-development/insights', {
        params: { period: period.value },
      });
      insights.value = result<DevelopmentInsights>(data) ?? null;
    } catch (e) {
      handleResponseException(e);
    } finally {
      loading.value = false;
    }
  };

  return { insights, period, loading, load };
}
